/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { PrimaryButton } from '@fluentui/react/lib/Button';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DeleteButton } from 'src/core/components/DeleteButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { Tile } from 'src/core/components/Tile';
import { TileContent } from 'src/core/components/TileContent';
import { TileFooter } from 'src/core/components/TileFooter';
import { TileHeader } from 'src/core/components/TileHeader';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { DomainState } from 'src/luis/types/stores/domains/DomainStore';

export type DomainCardProps = {
    /**
     * The title of the prebuilt domain
     */
    name: string;

    /**
     * The description of the prebuilt domain
     */
    description: string;

    /**
     * The state of the card button
     */
    state: DomainState;

    /**
     * Contains the styles for this component
     */
    className?: string;

    /**
     * The handler function to manage the button clicks
     */
    onCardClicked: () => void;

    /**
     * Indicates whether  the Add/remove button is disabled or not
     */
    isAddorRemoveDisabled: boolean;

    /**
     * User role to show in the luis tool tip.
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    addButton: {
        id: 'domains.domainCard.addButton',
        defaultMessage: 'Add domain'
    },
    removeButton: {
        id: 'domains.domainCard.removeButton',
        defaultMessage: 'Remove domain'
    },
    learnMoreDetailed: {
        id: 'domains.domainCard.learnMoreDetailed',
        defaultMessage: 'Learn more about the { domainName } domain.'
    }
});

const domainCardTheme = getScopedTheme('DomainCard');

/**
 * Component used for prebuilt domains cards.
 */
export const DomainCard = (props: DomainCardProps) => {
    const { name, description, state, className, onCardClicked, isAddorRemoveDisabled, displayedUserRole } = props;
    const { localize } = useLocalization();

    /**
     * Gets the button to show based on the state of the card.
     */
    const getCardButton = () => {
        switch (state) {
            case 'notAdded':
                return (
                    <RBACTooltip
                        hidden={!isAddorRemoveDisabled}
                        displayedUserRole={displayedUserRole}
                        componentSurroundedByToolTip={
                            <PrimaryButton text={localize(messages.addButton)} onClick={onCardClicked} disabled={isAddorRemoveDisabled} />
                        }
                    />
                );
            case 'added':
                return (
                    <RBACTooltip
                        hidden={!isAddorRemoveDisabled}
                        displayedUserRole={displayedUserRole}
                        componentSurroundedByToolTip={
                            <DeleteButton text={localize(messages.removeButton)} onClick={onCardClicked} disabled={isAddorRemoveDisabled} />
                        }
                    />
                );
            default:
                return <PrimaryButton text={localize(globalMessages.loading)} disabled />;
        }
    };

    return (
        <div className={className}>
            <Tile>
                <TileHeader>
                    <LuisText variant="large" fontWeight={domainCardTheme.titleFontWeight as Property.FontWeight}>
                        {name}
                    </LuisText>
                </TileHeader>

                <TileContent>
                    <LuisText variant="medium" lineHeight="1.5">
                        {description}
                        &nbsp;
                        <ExternalLink
                            aria-label={localize(messages.learnMoreDetailed, { domainName: name })}
                            href={`https://docs.microsoft.com/azure/cognitive-services/luis/luis-reference-prebuilt-domains#${name}`}>
                            {localize(globalMessages.learnMore)}
                        </ExternalLink>
                    </LuisText>
                </TileContent>

                <TileFooter>{getCardButton()}</TileFooter>
            </Tile>
        </div>
    );
};
